import { useRef, useState, useEffect } from 'react';

import styled from 'styled-components';
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  Area, AreaChart, CartesianGrid
} from 'recharts';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import CustomScrollbar from '../../Share/Normal/CustomScrollbar';

export default function AttendanceChart(props) {
  const { chartData, viewMode, isCountOncePerDay } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [chartOpacity, setChartOpacity] = useState(1);
  const [renderData, setRenderData] = useState(chartData);

  const scrollRef = useRef(null);

  const maxRate = chartData.length > 0 ? Math.max(...chartData.map(d => d.rate)) : 0;
  const rawMax = maxRate > 0 ? maxRate * 1.15 : 10;
  const maxLines = 5;
  const rawStep = rawMax / maxLines;
  const stepBase = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const step = Math.ceil(rawStep / stepBase) * stepBase;
  const maximum = step * maxLines;
  const ticks = Array.from({ length: maxLines + 1 }, (_, i) => i * step);

  // viewMode 전환 시 페이드 아웃 → 데이터 교체 → 페이드 인 //
  useEffect(() => {
    setChartOpacity(0);

    const timer = setTimeout(() => {
      setRenderData(chartData);
      setChartOpacity(1);
    }, 200);

    return () => clearTimeout(timer);
  }, [chartData]);

  // renderData 변경 후 스크롤 위치 리셋 //
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollLeft = 0;
  }, [renderData]);

  // 터치패드 상하 스크롤을 가로 스크롤로 변환 //
  useEffect(() => {
    const el = scrollRef.current;

    if (!el) return;

    const handler = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY * 0.5;
    };

    el.addEventListener('wheel', handler, { passive: false });

    return () => el.removeEventListener('wheel', handler);
  }, []);

  // 커스텀 툴팁 //
  function CustomTooltip(props) {
    const { active, payload } = props;

    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;

    return (
      <TooltipBox>
        <TooltipLabelBox>
          <TooltipText fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.text}>
            {data.tooltipLabel}
          </TooltipText>
        </TooltipLabelBox>
        <TooltipText fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.brand500}>
          {data.rate.toFixed(1)}%
        </TooltipText>
        <TooltipDetailRow>
          <TooltipText fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.subText}>
            {t('statistics.attendance.attendees')}
          </TooltipText>
          <TooltipText fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.brand500}>
            {data.attendees.toLocaleString('ko-KR')}
          </TooltipText>
        </TooltipDetailRow>
        <TooltipDetailRow>
          <TooltipText fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.subText}>
            {t('statistics.attendance.validMembers')}
          </TooltipText>
          <TooltipText fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.brand500}>
            {data.validMembers.toLocaleString('ko-KR')}
          </TooltipText>
        </TooltipDetailRow>
      </TooltipBox>
    );
  }

  // 커스텀 커서 (세로 막대선) //
  function CustomCursor(props) {
    const { points } = props;

    if (!points || !points.length) return null;

    const x = points[0].x;
    const startY = points[0].y + 30;
    const endY = points[1].y;

    return (
      <line
        x1={x} y1={startY}
        x2={x} y2={endY}
        stroke={theme.line}
        strokeWidth={2}
        strokeLinecap='round'
      />
    );
  }

  // 커스텀 도트 //
  function CustomDot(props) {
    const { cx, cy } = props;

    if (cx === undefined || cy === undefined) return null;

    return (
      <circle cx={cx} cy={cy} r={3.5} fill={theme.brand500} stroke='none' />
    );
  }

  // 고정 Y축 라벨 렌더링 (recharts plot area: top margin 20 + XAxis 기본 height 30 → 차트 높이 260) //
  function renderFixedYAxisLabels() {
    const plotTop = 20;
    const plotHeight = 260;

    return ticks.map((tick, index) => {
      const y = plotTop + (1 - tick / maximum) * plotHeight;
      const tickLabel = Number.isInteger(tick) ? tick : Math.round(tick * 1000) / 1000;

      return (
        <YAxisLabelText key={'YAxisLabel-' + index} $top={y}>
          {tickLabel}
        </YAxisLabelText>
      );
    });
  }

  return (
    <ChartWrapper>
      <ChartContentWrapper $opacity={chartOpacity}>
        <ChartScrollContainer ref={scrollRef}>
          <ChartInner $width={renderData.length > 15 ? renderData.length * 30 : undefined}>
            <ResponsiveContainer width="100%" height={310}>
              <AreaChart
                data={renderData}
                margin={{ top: 20, right: 5, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="attendanceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.brand500} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={theme.brand500} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  horizontal={true}
                  vertical={false}
                  stroke={theme.line}
                  strokeDasharray="none"
                />
                <XAxis
                  dataKey="label"
                  axisLine={{ stroke: theme.line }}
                  tickLine={false}
                  tick={{ fontFamily: 'NotoSansKR-Medium', fontSize: 14, fill: theme.text }}
                  padding={{ left: 30, right: 30 }}
                />
                <YAxis
                  domain={[0, maximum]}
                  ticks={ticks}
                  axisLine={false}
                  tickLine={false}
                  tick={true}
                  width={60}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={<CustomCursor />}
                  position={{ y: 0 }}
                  offset={-11}
                  allowEscapeViewBox={{ x: false, y: true }}
                />
                <Area
                  type="monotone"
                  dataKey="rate"
                  stroke={theme.brand500}
                  strokeWidth={2}
                  fill="url(#attendanceGradient)"
                  dot={<CustomDot />}
                  activeDot={{ r: 3.5, fill: theme.brand500, stroke: theme.brand500 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartInner>
        </ChartScrollContainer>
        <FixedYAxisBox>
          {renderFixedYAxisLabels()}
        </FixedYAxisBox>
      </ChartContentWrapper>
      <ScrollbarWrapper>
        <CustomScrollbar
          key={renderData.length + '-' + (renderData[0]?.label || '') + '-' + isCountOncePerDay}
          direction="horizontal"
          scrollRef={scrollRef}
          thumbSize={100}
          thumbColor={theme.brand500}
          showTrackAlways={false}
        />
      </ScrollbarWrapper>
    </ChartWrapper>
  );
}

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  margin-top: 20px;

  & svg {
    cursor: pointer !important;
  }

  & *:focus,
  & *:focus-visible {
    outline: none;
  }
`;

const ChartContentWrapper = styled.div`
  position: relative;

  width: 100%;
  height: 310px;

  opacity: ${props => props.$opacity};
  transition: opacity 0.1s ease-in-out;
`;

const FixedYAxisBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;

  width: 50px;
  height: 310px;

  background-color: ${props => props.theme.subBackground};

  pointer-events: none;
`;

const YAxisLabelText = styled.p`
  position: absolute;
  top: ${props => props.$top}px;
  right: 5px;

  transform: translateY(-50%);

  font-family: NotoSansKR-Medium;
  font-size: 16px;

  color: ${props => props.theme.text};

  margin: 0;
`;

const ChartScrollContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
`;

const ChartInner = styled.div`
  min-width: ${props => props.$width ? props.$width + 'px' : '100%'};
`;

const ScrollbarWrapper = styled.div`
  width: 100%;
  height: 15px;
`;


const TooltipBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 10px;

  gap: 5px;

  border-radius: 5px;
  background-color: ${props => props.theme.background};
  box-shadow: 0px 4px 50px 0px rgba(0, 0, 0, 0.05);

  white-space: nowrap;
`;

const TooltipLabelBox = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 100%;

  border-radius: 2px;
  background-color: ${props => props.theme.buttonFill};
`;

const TooltipDetailRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;

  gap: 10px;
`;

const TooltipText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;
